import { useEffect } from 'react';
import { useLocation } from 'wouter';

interface MetaTagsProps {
  title?: string;
  description?: string;
  keywords?: string;
}

const defaultMeta = {
  title: 'OneGov - Legislative Intelligence Platform',
  description: 'Real-time legislative intelligence, comprehensive advocacy management, and AI-powered analytics across all 50 states.',
  keywords: 'legislative intelligence, policy tracking, advocacy management, lobbying tools, bill tracking'
};

const routeMeta: Record<string, { title: string; description: string; keywords?: string }> = {
  '/': {
    title: 'OneGov - Real-Time Legislative Intelligence & Advocacy Platform',
    description: 'Transform your legislative strategy with AI-powered analytics, real-time transcripts, and comprehensive advocacy tools for all 50 states. Track bills, manage relationships, and stay ahead.',
    keywords: 'legislative intelligence, policy tracking, advocacy management, AI analytics, bill tracking, government relations'
  },
  '/solutions/associations': {
    title: 'OneGov for Associations - Multi-State Legislative Management',
    description: 'Empower your association with comprehensive legislative tracking across all states. Manage member advocacy, track priority bills, and coordinate grassroots campaigns from one unified platform.',
    keywords: 'association management, trade association software, legislative tracking for associations, multi-state advocacy, member engagement'
  },
  '/solutions/corporations': {
    title: 'OneGov for Corporations - Enterprise Government Affairs Platform',
    description: 'Enterprise-grade legislative intelligence for corporate government affairs teams. Monitor regulatory changes, manage compliance risks, and coordinate multi-state advocacy efforts efficiently.',
    keywords: 'corporate government affairs, enterprise legislative tracking, regulatory compliance, corporate advocacy, government relations software'
  },
  '/solutions/lobbyists': {
    title: 'OneGov for Lobbyists - Professional Advocacy Tools',
    description: 'Professional-grade tools for lobbyists and government relations firms. Track client bills, manage legislator relationships, generate reports, and demonstrate value with comprehensive analytics.',
    keywords: 'lobbying software, government relations tools, legislative tracking for lobbyists, client management, advocacy reporting'
  },
  '/why-onegov': {
    title: 'Why Choose OneGov - Leading Legislative Intelligence Platform',
    description: 'Discover why top advocacy professionals choose OneGov. Real-time transcripts, AI-powered insights, comprehensive coverage of all 50 states, and proven ROI for your advocacy efforts.',
    keywords: 'why onegov, legislative intelligence benefits, advocacy platform comparison, government affairs software'
  },
  '/features': {
    title: 'OneGov Features - Comprehensive Legislative Intelligence Tools',
    description: 'Explore OneGov\'s powerful features: real-time transcripts, AI bill analysis, advocate management, automated reporting, committee tracking, and integrated communication tools.',
    keywords: 'legislative tracking features, bill tracking tools, advocacy software features, government relations platform'
  },
  '/about': {
    title: 'About OneGov - Your Partner in Legislative Intelligence',
    description: 'Learn about OneGov\'s mission to democratize legislative intelligence. Our team combines deep policy expertise with cutting-edge AI to deliver actionable insights for advocacy professionals.',
    keywords: 'about onegov, legislative intelligence company, advocacy technology, government affairs innovation'
  },
  '/products/bill-agent': {
    title: 'Bill Agent - AI-Powered Legislative Analysis',
    description: 'Automate bill tracking with our AI Bill Agent. Get instant summaries, impact analysis, and stakeholder insights for any legislation across all 50 states.',
    keywords: 'AI bill tracking, automated legislative analysis, bill summary tool, legislative AI'
  },
  '/products/committee-agent': {
    title: 'Committee Agent - Real-Time Meeting Intelligence',
    description: 'Never miss critical committee actions. Our AI Committee Agent monitors hearings, transcribes proceedings, and alerts you to relevant discussions in real-time.',
    keywords: 'committee tracking, hearing transcripts, legislative committee monitoring, real-time alerts'
  },
  '/products/auto-track': {
    title: 'AutoTrack - Intelligent Bill Discovery',
    description: 'Automatically discover and track relevant legislation using AI. Set your criteria and let AutoTrack find bills that matter to your organization across all jurisdictions.',
    keywords: 'automatic bill tracking, legislative discovery, AI bill monitoring, smart tracking'
  },
  '/pricing': {
    title: 'OneGov Pricing - Flexible Plans for Every Organization',
    description: 'Transparent pricing for legislative intelligence. Choose from plans designed for associations, corporations, and professional lobbyists. Start with our free trial.',
    keywords: 'onegov pricing, legislative tracking cost, advocacy platform pricing, government affairs software cost'
  },
  '/demo': {
    title: 'Schedule a OneGov Demo - See the Platform in Action',
    description: 'Book a personalized demo of OneGov. See how our platform can transform your legislative tracking and advocacy efforts. Free consultation with our experts.',
    keywords: 'onegov demo, legislative software demo, advocacy platform demonstration, free consultation'
  }
};

export default function MetaTags({ title, description, keywords }: MetaTagsProps) {
  const [location] = useLocation();

  useEffect(() => {
    // Get route-specific meta or use provided/default values
    const routeMetaData = routeMeta[location] || {};
    const finalTitle = title || routeMetaData.title || defaultMeta.title;
    const finalDescription = description || routeMetaData.description || defaultMeta.description;
    const finalKeywords = keywords || routeMetaData.keywords || defaultMeta.keywords;

    // Update title
    document.title = finalTitle;

    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', finalDescription);

    // Update meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content', finalKeywords);

    // Update OG tags
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (!ogTitle) {
      ogTitle = document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      document.head.appendChild(ogTitle);
    }
    ogTitle.setAttribute('content', finalTitle);

    let ogDescription = document.querySelector('meta[property="og:description"]');
    if (!ogDescription) {
      ogDescription = document.createElement('meta');
      ogDescription.setAttribute('property', 'og:description');
      document.head.appendChild(ogDescription);
    }
    ogDescription.setAttribute('content', finalDescription);

  }, [location, title, description, keywords]);

  return null;
}